/**
 * Supabase data layer for the vacant.day calendar.
 * Implements the same 16-function interface as the old data.ts.
 *
 * All data is prefetched once at module load via top-level await,
 * then filtered in-memory — keeping the API synchronous.
 */

import { createClient } from '@supabase/supabase-js';
import { unstable_cache } from 'next/cache';
import type { Database } from './database.types';
import { fetchAll, fetchAllUntyped } from './db-helpers';
import { logError, errFields } from './logger';
import { preloadPhenomena } from './db-phenomena';
import { preloadBestTime } from './db-best-time';
import type { AlmanakTableName, RowOf } from './db-helpers';
import type { CalendarEvent, Lens, Country, City, University, CountryCode, LensId, SubLensId, ReligionSubLensId, VisaRequirement, VisaCategory } from './types';
import { isPublicHoliday } from './event-filters';
import { parseLocalDate } from './date-calc';

// --- Generated row type aliases ---

type CalEventsRow = RowOf<'cal_events'>;
type CalCountriesRow = RowOf<'cal_countries'>;
type CalCitiesRow = RowOf<'cal_cities'>;
type CalUniversitiesRow = RowOf<'cal_universities'>;
type I18nLanguagesRow = RowOf<'i18n_languages'>;
type I18nCountryNamesRow = RowOf<'i18n_country_names'>;
type I18nPhrasesRow = RowOf<'i18n_phrases'>;
type I18nCityNamesRow = RowOf<'i18n_city_names'>;
type I18nTemplatesRow = RowOf<'i18n_templates'>;
type CalDestinationMonthsRow = RowOf<'cal_destination_months'>;
type CalNatureMetadataRow = RowOf<'cal_nature_metadata'>;
type CalOriginHolidaysRow = RowOf<'cal_origin_holidays'>;
type VisaRequirementsRow = RowOf<'visa_requirements'>;
type CalReligionsRow = RowOf<'cal_religions'>;
type CalReligionCountriesRow = RowOf<'cal_religion_countries'>;
type CalOrganizationsRow = RowOf<'cal_organizations'>;
type CalOrganizationCountriesRow = RowOf<'cal_organization_countries'>;
type I18nOrganizationNamesRow = RowOf<'i18n_organization_names'>;
type I18nSubdivisionNamesRow = RowOf<'i18n_subdivision_names'>;

// --- I18n types ---

export interface I18nLanguage {
  code: string;
  label: string;
  labelEn: string | null;
  isGlobal: boolean;
  isRtl: boolean;
  isActive: boolean;
  hreflang: string | null;
  sortOrder: number;
  googleTranslateCode: string | null;
}

export interface I18nCountryName {
  countryCode: CountryCode;
  lang: string;
  display: string;
  slug: string;
}

export interface I18nPhrase {
  phraseKey: string;
  lang: string;
  display: string;
  slug: string;
}

export interface I18nCityName {
  cityId: string;
  lang: string;
  display: string;
  slug: string;
}

export interface I18nTemplate {
  templateKey: string;
  lang: string;
  template: string;
}

export interface I18nSubdivisionName {
  subdivision: string;
  lang: string;
  display: string;
}

export interface I18nCountryLanguage {
  countryCode: string;
  lang: string;
  isPrimary: boolean;
}
import { ui as uiFn } from './i18n';
import { getLongWeekends } from './long-weekends';

// --- Religion demographics types ---

export interface Religion {
  id: string;
  name: string;
  subLensId: string | null;
  sortOrder: number;
}

export interface ReligionCountry {
  religionId: string;
  countryCode: CountryCode;
  adherents: number | null;
  percentage: number | null;
  source: string | null;
}

// --- Organization types ---

export type OrgType = 'government' | 'religious_council' | 'community' | 'temple_board' | 'advocacy';
export type AuthorityScope = 'all_dates' | 'lunar_sighting' | 'gazetted_holidays' | 'community_calendar';

export interface Organization {
  id: string;
  religionId: string;
  name: string;
  abbreviation: string | null;
  orgType: OrgType;
  website: string | null;
  logoUrl: string | null;
  foundedYear: number | null;
  description: string | null;
  sortOrder: number;
}

export interface OrganizationCountry {
  organizationId: string;
  countryCode: CountryCode;
  isDateAuthority: boolean;
  authorityScope: AuthorityScope | null;
  establishedYear: number | null;
  source: string | null;
}

export interface OrganizationName {
  organizationId: string;
  lang: string;
  display: string;
  slug: string;
}

// --- Nature metadata types ---

export interface NatureMetadata {
  eventId: string;
  peakStart: string;
  peakEnd: string;
  varianceDays: number | null;
  earliestStart: string | null;
  latestEnd: string | null;
  source: string | null;
  sourceUrl: string | null;
  regions: Array<{ region_name: string; city_codes?: string[]; peak_start?: string; peak_end?: string }> | null;
}

// --- Origin holiday types ---

export interface OriginHoliday {
  countryCode: string;
  date: string;
  name: string;
  type: 'public_holiday' | 'school_holiday';
  createsLongWeekend: boolean;
}

// --- Destination month types ---

export interface DestinationMonth {
  destinationCode: string;
  month: number;
  weatherScore: number;
  crowdLevel: 'low' | 'medium' | 'high';
  priceLevel: 'low' | 'medium' | 'high';
  tempRange: string;
  seasonName: string;
  weatherSummary: string;
  visitVerdict: string;
}

// Lenses are app configuration, not DB data — keep as JSON import
import lensesData from '@/data/lenses.json';

// --- Supabase client ---

const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!url || !key) {
  throw new Error('Missing NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY');
}

const sb = createClient<Database, 'almanak'>(url, key, { db: { schema: 'almanak' } });

// --- Row mappers ---

function rowToEvent(row: CalEventsRow): CalendarEvent {
  const startDate = parseLocalDate(row.date_start);
  const endDate = parseLocalDate(row.date_end);
  return {
    id: row.id,
    title: row.title,
    titleLocal: row.title_local || undefined,
    dateStart: row.date_start,
    dateEnd: row.date_end,
    year: startDate.getFullYear(),
    startMs: startDate.getTime(),
    endMs: endDate.getTime(),
    startDay: startDate.getDate(),
    country: row.country as CountryCode,
    city: row.city || undefined,
    lens: row.lens as LensId,
    subLens: (row.sub_lens as SubLensId) || undefined,
    type: row.event_type as CalendarEvent['type'],
    significance: row.significance as CalendarEvent['significance'],
    notes: row.notes || undefined,
    dateUncertainty: row.date_uncertainty ?? undefined,
    color: row.color,
    isGazetted: row.is_gazetted === true ? true : undefined,
    planningLeadTime: row.planning_lead_time || undefined,
    businessImpact: row.business_impact || undefined,
    businessImpactLevel: (row.business_impact_level as CalendarEvent['businessImpactLevel']) || undefined,
    complianceRisk: row.compliance_risk || undefined,
    imageUrl: row.image_url || undefined,
    sourceUrl: row.source_url || undefined,
    sourceName: row.source_name || undefined,
    sourceType: (row.source_type as CalendarEvent['sourceType']) || undefined,
    visibility: row.visibility as CalendarEvent['visibility'],
    eventGroup: row.event_group || undefined,
    universityId: row.university_id || undefined,
    calculationMethod: row.calculation_method || undefined,
    verifiedDate: row.verified_date || undefined,
    impactSeverity: (row.impact_severity as CalendarEvent['impactSeverity']) || undefined,
    subdivision: row.subdivision || undefined,
    campaignSensitivity: row.campaign_sensitivity ?? undefined,
    prepWindowDays: row.prep_window_days ?? undefined,
    commercialWindowDays: row.commercial_window_days ?? undefined,
    bookingPressure: (row.booking_pressure as CalendarEvent['bookingPressure']) || undefined,
    validityScore: row.validity_score ?? undefined,
    updatedAt: row.updated_at ?? undefined,
  };
}

function rowToCountry(row: CalCountriesRow): Country {
  return {
    code: row.code as CountryCode,
    name: row.name,
    flag: row.flag,
    region: row.region,
    localLang: row.local_lang || undefined,
    iso3: row.iso3 || undefined,
    isoNumeric: row.iso_numeric || undefined,
    capital: row.capital || undefined,
    areaKm2: row.area_km2 || undefined,
    population: row.population || undefined,
    continent: row.continent || undefined,
    tld: row.tld || undefined,
    currencyCode: row.currency_code || undefined,
    currencyName: row.currency_name || undefined,
    phoneCode: row.phone_code || undefined,
    languages: row.languages || undefined,
    geonamesId: row.geonames_id || undefined,
    neighbours: row.neighbours || undefined,
    timezone: row.timezone || undefined,
    isDisplayed: row.is_displayed,
    imageUrl: row.image_url || undefined,
  };
}

function rowToI18nLanguage(row: I18nLanguagesRow): I18nLanguage {
  // is_active and google_translate_code exist in DB but aren't in the generated schema yet
  const ext = row as I18nLanguagesRow & { is_active?: boolean; google_translate_code?: string | null };
  return {
    code: row.code,
    label: row.label,
    labelEn: row.label_en || null,
    isGlobal: row.is_global,
    isRtl: row.is_rtl,
    isActive: ext.is_active ?? true,
    hreflang: row.hreflang || null,
    sortOrder: row.sort_order,
    googleTranslateCode: ext.google_translate_code || null,
  };
}

function rowToI18nCountryName(row: I18nCountryNamesRow): I18nCountryName {
  return {
    countryCode: row.country_code as CountryCode,
    lang: row.lang,
    display: row.display,
    slug: row.slug,
  };
}

function rowToI18nPhrase(row: I18nPhrasesRow): I18nPhrase {
  return {
    phraseKey: row.phrase_key,
    lang: row.lang,
    display: row.display,
    slug: row.slug,
  };
}

function rowToI18nCityName(row: I18nCityNamesRow): I18nCityName {
  return {
    cityId: row.city_id,
    lang: row.lang,
    display: row.display,
    slug: row.slug,
  };
}

function rowToI18nSubdivisionName(row: I18nSubdivisionNamesRow): I18nSubdivisionName {
  return {
    subdivision: row.subdivision,
    lang: row.lang,
    display: row.display,
  };
}

function rowToI18nTemplate(row: I18nTemplatesRow): I18nTemplate {
  return {
    templateKey: row.template_key,
    lang: row.lang,
    template: row.template,
  };
}

function rowToI18nCountryLanguage(row: RowOf<'i18n_country_languages'>): I18nCountryLanguage {
  return {
    countryCode: row.country_code,
    lang: row.lang,
    isPrimary: row.is_primary,
  };
}

function rowToCity(row: CalCitiesRow): City {
  return {
    id: row.id,
    name: row.name,
    country: row.country as CountryCode,
    timezone: row.timezone,
    icon: row.icon,
    geonamesId: row.geonames_id || undefined,
    asciiName: row.ascii_name || undefined,
    admin1Code: row.admin1_code || undefined,
    lat: row.lat || undefined,
    lng: row.lng || undefined,
    population: row.population || undefined,
    elevation: row.elevation || undefined,
    isDisplayed: row.is_displayed ?? true,
    imageUrl: row.image_url || undefined,
    logoUrl: (row as CalCitiesRow & { logo_url?: string | null }).logo_url || undefined,
  };
}

function rowToUniversity(row: CalUniversitiesRow): University {
  // city_id, website, admission_url, address, academic_portal columns exist in DB but aren't in the generated schema yet
  const ext = row as CalUniversitiesRow & { city_id?: string | null; website?: string | null; admission_url?: string | null; street_address?: string | null; postal_code?: string | null; address_region?: string | null; academic_portal_url?: string | null; academic_portal_name?: string | null; academic_portal_desc_id?: string | null; academic_portal_desc_en?: string | null; type?: string | null; website_url?: string | null; pddikti_id?: string | null; latitude?: number | null; longitude?: number | null; social_instagram?: string | null; social_twitter?: string | null; social_youtube?: string | null; social_tiktok?: string | null; social_facebook?: string | null };
  return {
    id: row.id,
    name: row.name,
    abbrev: row.abbrev,
    city: row.city,
    cityId: ext.city_id || undefined,
    country: row.country as CountryCode,
    icon: row.icon,
    students: row.students,
    sourceUrl: row.source_url || undefined,
    website: ext.website || undefined,
    admissionUrl: ext.admission_url || undefined,
    imageUrl: row.image_url || undefined,
    logoUrl: (row as CalUniversitiesRow & { logo_url?: string | null }).logo_url || undefined,
    streetAddress: ext.street_address || undefined,
    postalCode: ext.postal_code || undefined,
    addressRegion: ext.address_region || undefined,
    academicPortalUrl: ext.academic_portal_url || undefined,
    academicPortalName: ext.academic_portal_name || undefined,
    academicPortalDescId: ext.academic_portal_desc_id || undefined,
    academicPortalDescEn: ext.academic_portal_desc_en || undefined,
    type: ext.type || undefined,
    websiteUrl: ext.website_url || undefined,
    pddiktiId: ext.pddikti_id || undefined,
    latitude: ext.latitude ?? undefined,
    longitude: ext.longitude ?? undefined,
    socialInstagram: ext.social_instagram || undefined,
    socialTwitter: ext.social_twitter || undefined,
    socialYoutube: ext.social_youtube || undefined,
    socialTiktok: ext.social_tiktok || undefined,
    socialFacebook: ext.social_facebook || undefined,
  };
}

// Extended event with DB-only fields used for filtering
interface InternalEvent extends CalendarEvent {
  _year: number;
  _vertical: string;
  _universityId: string | null;
}

/**
 * Collapse duplicate cal_events rows by primary key (`id`), keeping the first
 * occurrence. Guards against PostgREST page-boundary duplication in the
 * paginated raw fetch (see _setEventsData). Order-preserving so the first row
 * for each id wins, matching the previous map-based _eventsById behaviour.
 * Refs: almanak-n8f7
 */
function dedupeEventRowsById(rows: CalEventsRow[]): CalEventsRow[] {
  const seen = new Set<string>();
  const out: CalEventsRow[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

function rowToInternalEvent(row: CalEventsRow): InternalEvent {
  return {
    ...rowToEvent(row),
    _year: row.year,
    _vertical: row.vertical,
    _universityId: row.university_id ?? null,
  };
}

function rowToOriginHoliday(row: CalOriginHolidaysRow): OriginHoliday {
  return {
    countryCode: row.country_code,
    date: row.date,
    name: row.name,
    type: row.type as 'public_holiday' | 'school_holiday',
    createsLongWeekend: row.creates_long_weekend === true,
  };
}

function rowToNatureMetadata(row: CalNatureMetadataRow): NatureMetadata {
  return {
    eventId: row.event_id,
    peakStart: row.peak_start,
    peakEnd: row.peak_end,
    varianceDays: row.variance_days ?? null,
    earliestStart: row.earliest_start || null,
    latestEnd: row.latest_end || null,
    source: row.source || null,
    sourceUrl: row.source_url || null,
    regions: row.regions ? (row.regions as NatureMetadata['regions']) : null,
  };
}

function rowToDestinationMonth(row: CalDestinationMonthsRow): DestinationMonth {
  return {
    destinationCode: row.destination_code,
    month: row.month,
    weatherScore: row.weather_score,
    crowdLevel: row.crowd_level as 'low' | 'medium' | 'high',
    priceLevel: row.price_level as 'low' | 'medium' | 'high',
    tempRange: row.temp_range,
    seasonName: row.season_name,
    weatherSummary: row.weather_summary,
    visitVerdict: row.visit_verdict,
  };
}

function rowToReligion(row: CalReligionsRow): Religion {
  return {
    id: row.id,
    name: row.name,
    subLensId: row.sub_lens_id || null,
    sortOrder: row.sort_order,
  };
}

function rowToOrganization(row: CalOrganizationsRow): Organization {
  return {
    id: row.id,
    religionId: row.religion_id,
    name: row.name,
    abbreviation: row.abbreviation || null,
    orgType: row.org_type as OrgType,
    website: row.website || null,
    logoUrl: row.logo_url || null,
    foundedYear: row.founded_year ?? null,
    description: row.description || null,
    sortOrder: row.sort_order,
  };
}

function rowToOrganizationCountry(row: CalOrganizationCountriesRow): OrganizationCountry {
  return {
    organizationId: row.organization_id,
    countryCode: row.country_code as CountryCode,
    isDateAuthority: row.is_date_authority,
    authorityScope: (row.authority_scope as AuthorityScope) || null,
    establishedYear: row.established_year ?? null,
    source: row.source || null,
  };
}

function rowToOrganizationName(row: I18nOrganizationNamesRow): OrganizationName {
  return {
    organizationId: row.organization_id,
    lang: row.lang,
    display: row.display,
    slug: row.slug,
  };
}

function rowToReligionCountry(row: CalReligionCountriesRow): ReligionCountry {
  return {
    religionId: row.religion_id,
    countryCode: row.country_code as CountryCode,
    adherents: row.adherents ?? null,
    percentage: row.percentage ?? null,
    source: row.source || null,
  };
}

function rowToVisaRequirement(row: VisaRequirementsRow): VisaRequirement {
  return {
    id: row.id,
    origin: row.origin,
    destination: row.destination,
    visaCategory: row.visa_category as VisaCategory,
    durationDays: row.duration_days ?? null,
    costUsd: row.cost_usd ?? null,
    notes: row.notes || null,
    sourceName: row.source_name || null,
    lastVerified: row.last_verified,
  };
}

// --- Mutable data state ---
//
// Populated by the _set*Data setters which are called from the preload*()
// helpers (preloadI18n / preloadEvents / preloadGeo / preloadConfig /
// preloadVisa). Until a preload runs for a given group, the arrays stay
// empty and the sync getters return empty results — server-component entry
// points must await the relevant preload before reading.

let _events: InternalEvent[] = [];
let _countries: Country[] = [];
let _cities: City[] = [];
let _universities: University[] = [];
let _i18nLanguages: I18nLanguage[] = [];
let _i18nCountryNames: I18nCountryName[] = [];
let _i18nPhrases: I18nPhrase[] = [];
let _i18nCityNames: I18nCityName[] = [];
let _i18nSubdivisionNames: I18nSubdivisionName[] = [];
let _i18nTemplates: I18nTemplate[] = [];
let _destinationMonths: DestinationMonth[] = [];
let _natureMetadata: NatureMetadata[] = [];
let _originHolidays: OriginHoliday[] = [];
let _visaRequirements: VisaRequirement[] = [];
let _religions: Religion[] = [];
let _religionCountries: ReligionCountry[] = [];
let _i18nCountryLanguages: I18nCountryLanguage[] = [];
let _organizations: Organization[] = [];
let _organizationCountries: OrganizationCountry[] = [];
let _organizationNames: OrganizationName[] = [];
let _publicEvents: CalendarEvent[] = [];

let _adminSettingsRaw: Record<string, unknown>[] = [];
let _pageTypesRaw: RowOf<'page_types'>[] = [];
let _groupMetaRaw: RowOf<'cal_event_group_metadata'>[] = [];
let _eventTranslationsRaw: RowOf<'cal_event_translations'>[] = [];

// --- Derived index types ---

export interface NavConfig {
  showLanguage: boolean;
  showCountry: boolean;
  showCity: boolean;
  showBrowse: boolean;
  showSearch: boolean;
}

export interface EventGroupMetadata {
  eventGroup: string;
  displayName: string;
  description: string | null;
}

// --- Derived indexes (cleared + repopulated by _rebuildIndexes) ---

const _pageTypesMap = new Map<string, RowOf<'page_types'>>();
const _adminSettings = new Map<string, string>();
const _visaReqMap = new Map<string, VisaRequirement>();
const _visaReqByOrigin = new Map<string, VisaRequirement[]>();
const _groupMetaMap = new Map<string, EventGroupMetadata>();
const _countriesByCode = new Map<string, Country>();
const _citiesById = new Map<string, City>();
const _universitiesById = new Map<string, University>();
const _eventsById = new Map<string, CalendarEvent>();
const _eventsByReligionSubLens = new Map<string, CalendarEvent[]>();
const _eventsByReligionCountry = new Map<string, CalendarEvent[]>();
const _eventsByNatureCountry = new Map<string, CalendarEvent[]>();
const _allNatureEvents: CalendarEvent[] = [];
const _destMonthsByCode = new Map<string, DestinationMonth[]>();
const _destMonthByKey = new Map<string, DestinationMonth>();
const _natureMetaById = new Map<string, NatureMetadata>();
const _originHolidaysByCountry = new Map<string, OriginHoliday[]>();
const _relCountriesByCountry = new Map<string, ReligionCountry[]>();
const _relCountriesByReligion = new Map<string, ReligionCountry[]>();
const _orgsById = new Map<string, Organization>();
const _orgsByReligion = new Map<string, Organization[]>();
const _orgCountriesByCountry = new Map<string, OrganizationCountry[]>();
const _orgNamesByOrg = new Map<string, OrganizationName[]>();
const _eventsByUniYear = new Map<string, CalendarEvent[]>();
const _eventsByUni = new Map<string, CalendarEvent[]>();
const _yearsByUni = new Map<string, Set<number>>();
const _eventsByCity = new Map<string, CalendarEvent[]>();
const _eventsByCountry = new Map<string, CalendarEvent[]>();
const _publicHolidaysByCountry = new Map<string, CalendarEvent[]>();
const _eventsByGroup = new Map<string, CalendarEvent[]>();
const _cityNameMap = new Map<string, string>();
const _subdivNameMap = new Map<string, string>();
const _eventTranslationKeys = new Set<string>();
const _translatedTitles = new Map<string, string>();

// --- Public API for derived config ---

export function getNavConfig(pageType: string): NavConfig {
  const row = _pageTypesMap.get(pageType);
  return {
    showLanguage: row?.show_language ?? true,
    showCountry: row?.show_country ?? true,
    showCity: row?.show_city ?? true,
    showBrowse: row?.show_browse ?? true,
    showSearch: row?.show_search ?? true,
  };
}

/** Read an admin setting by key. Returns fallback if not set. */
export function getAdminSetting(key: string, fallback: string = ''): string {
  return _adminSettings.get(key) ?? fallback;
}

/** Get the minimum event threshold for a page type (defaults to 1). */
export function getMinEventsThreshold(pageType: string): number {
  return parseInt(getAdminSetting(`min_events.${pageType}`, '1'), 10);
}

// --- Module init ---
//
// Fully lazy as of qoy-6: nothing fetched at module load. Each consumer
// awaits one of the preload*() helpers (preloadI18n / preloadEvents /
// preloadGeo / preloadConfig / preloadVisa) before reading from the sync
// API. See .claude/docs/prd-db-supabase-lazy-init.md.

/**
 * Called by preloadI18n() to write the 7 i18n tables back into module-scope.
 * Rebuilds the derived _cityNameMap / _subdivNameMap that getCityName() and
 * getSubdivisionName() read from. Safe to call repeatedly (idempotent).
 */
export function _setI18nData(data: {
  languages: I18nLanguage[];
  countryNames: I18nCountryName[];
  phrases: I18nPhrase[];
  cityNames: I18nCityName[];
  subdivisionNames: I18nSubdivisionName[];
  templates: I18nTemplate[];
  countryLanguages: I18nCountryLanguage[];
}): void {
  _i18nLanguages = data.languages;
  _i18nCountryNames = data.countryNames;
  _i18nPhrases = data.phrases;
  _i18nCityNames = data.cityNames;
  _i18nSubdivisionNames = data.subdivisionNames;
  _i18nTemplates = data.templates;
  _i18nCountryLanguages = data.countryLanguages;

  _cityNameMap.clear();
  for (const cn of _i18nCityNames) _cityNameMap.set(`${cn.cityId}:${cn.lang}`, cn.display);
  _subdivNameMap.clear();
  for (const sn of _i18nSubdivisionNames) _subdivNameMap.set(`${sn.subdivision}:${sn.lang}`, sn.display);
}

/**
 * Called by preloadEvents() to write event + translation data into module
 * scope and rebuild the event-related derived indexes. Safe to call
 * repeatedly (idempotent).
 */
export function _setEventsData(data: {
  events: CalEventsRow[];
  eventTranslations: RowOf<'cal_event_translations'>[];
}): void {
  if (data.events.length === 0) {
    throw new Error('[db-supabase] cal_events returned 0 rows');
  }
  // Dedupe by event id before building any index. The raw cal_events fetch
  // paginates with PostgREST `.range()`; without a stable server-side sort the
  // same physical row can surface on the boundary of two pages once the table
  // exceeds one page (PAGE_SIZE=1000), yielding duplicate rows. Because the
  // per-country / per-group / per-university indexes below push every row into
  // plain arrays (only _eventsById is keyed), an undeduped duplicate renders
  // twice everywhere those arrays are consumed (homepage "Upcoming Public
  // Holidays", by-country comparison tables, etc.) with an identical slug/URL.
  // Collapsing here means every downstream getter is duplicate-free.
  // Refs: almanak-n8f7
  _events = dedupeEventRowsById(data.events).map(rowToInternalEvent);
  _eventTranslationsRaw = data.eventTranslations;
  _rebuildEventIndexes();
}

let _preloadEventsPromise: Promise<void> | null = null;

async function _doPreloadEvents(): Promise<void> {
  const [eventsRaw, eventTranslationsRaw] = await Promise.all([
    _cachedCalEventsRaw(),
    _cachedCalEventTranslationsRaw(),
  ]);
  _setEventsData({ events: eventsRaw, eventTranslations: eventTranslationsRaw });
}

/**
 * Load cal_events + cal_event_translations on demand. Server-component entry
 * points (layout.tsx, sitemap routes, page generateMetadata that runs outside
 * the layout) must `await preloadEvents()` before reading any sync event
 * getter (getEventsForCountry, getEventById, etc.). Backed by unstable_cache
 * (qoy-1b) so cross-request the actual fetch happens once per hour; the
 * Promise singleton dedupes concurrent calls within the process.
 */
export function preloadEvents(): Promise<void> {
  if (!_preloadEventsPromise) {
    _preloadEventsPromise = _doPreloadEvents().catch(err => {
      _preloadEventsPromise = null;
      logError('preload.events.failed', errFields(err));
      throw err;
    });
  }
  return _preloadEventsPromise;
}

/** Test-only: clear the preload singleton so a fresh preloadEvents() actually re-fetches. */
export function _resetPreloadEventsForTesting(): void {
  _preloadEventsPromise = null;
}

/**
 * Called by preloadGeo() to write country/city/university data into module
 * scope and rebuild the entity-by-ID derived maps. Safe to call repeatedly.
 */
export function _setGeoData(data: {
  countries: Country[];
  cities: City[];
  universities: University[];
}): void {
  _countries = data.countries;
  _cities = data.cities;
  _universities = data.universities;
  _rebuildGeoIndexes();
}

/** Country/city/university entity-by-ID map rebuild. Called by _setGeoData (qoy-4). */
function _rebuildGeoIndexes(): void {
  _countriesByCode.clear();
  _citiesById.clear();
  _universitiesById.clear();
  for (const c of _countries) _countriesByCode.set(c.code, c);
  for (const c of _cities) _citiesById.set(c.id, c);
  for (const u of _universities) _universitiesById.set(u.id, u);
}

let _preloadGeoPromise: Promise<void> | null = null;

async function _doPreloadGeo(): Promise<void> {
  const [countries, cities, universities] = await Promise.all([
    fetchCountriesAsync(),
    fetchCitiesAsync(),
    fetchUniversitiesAsync(),
  ]);
  _setGeoData({ countries, cities, universities });
}

/**
 * Load cal_countries + cal_cities + cal_universities on demand. Server-component
 * entry points (layout.tsx, sitemap routes, page generateMetadata that runs
 * outside the layout) must `await preloadGeo()` before reading any sync geo
 * getter (getCountries, getCountryByCode, getCityById, etc.). Backed by
 * unstable_cache (qoy-1b) cross-request; Promise singleton dedupes
 * concurrent in-process callers.
 */
export function preloadGeo(): Promise<void> {
  if (!_preloadGeoPromise) {
    _preloadGeoPromise = _doPreloadGeo().catch(err => {
      _preloadGeoPromise = null;
      logError('preload.geo.failed', errFields(err));
      throw err;
    });
  }
  return _preloadGeoPromise;
}

/** Test-only: clear the preload singleton so a fresh preloadGeo() actually re-fetches. */
export function _resetPreloadGeoForTesting(): void {
  _preloadGeoPromise = null;
}

/**
 * Called by preloadConfig() to write the 10 small config-shaped tables back
 * into module scope and rebuild their derived maps. Safe to call repeatedly.
 */
export function _setConfigData(data: {
  pageTypes: RowOf<'page_types'>[];
  adminSettings: Record<string, unknown>[];
  groupMeta: RowOf<'cal_event_group_metadata'>[];
  destinationMonths: DestinationMonth[];
  natureMetadata: NatureMetadata[];
  originHolidays: OriginHoliday[];
  religions: Religion[];
  religionCountries: ReligionCountry[];
  organizations: Organization[];
  organizationCountries: OrganizationCountry[];
  organizationNames: OrganizationName[];
}): void {
  _pageTypesRaw = data.pageTypes;
  _adminSettingsRaw = data.adminSettings;
  _groupMetaRaw = data.groupMeta;
  _destinationMonths = data.destinationMonths;
  _natureMetadata = data.natureMetadata;
  _originHolidays = data.originHolidays;
  _religions = data.religions;
  _religionCountries = data.religionCountries;
  _organizations = data.organizations;
  _organizationCountries = data.organizationCountries;
  _organizationNames = data.organizationNames;
  _rebuildConfigIndexes();
}

let _preloadConfigPromise: Promise<void> | null = null;

async function _doPreloadConfig(): Promise<void> {
  // phenomena + best_time live in their own loader modules but are config-like
  // small tables; kick them off concurrently so every preloadConfig() caller
  // (i.e. every evergreen-registry build path) also loads them. Refs: almanak-pju1.
  const phenomenaP = preloadPhenomena();
  const bestTimeP = preloadBestTime();
  // admin_settings is fetched untyped because it can be missing in
  // development; the other 10 use qoy-1b's cached typed fetchers.
  const [
    pageTypes,
    adminSettings,
    groupMeta,
    destinationMonths,
    natureMetadata,
    originHolidays,
    religions,
    religionCountries,
    organizations,
    organizationCountries,
    organizationNames,
  ] = await Promise.all([
    _cachedPageTypesRaw(),
    fetchAllUntyped(sb, 'admin_settings').catch(() => [] as Record<string, unknown>[]),
    _cachedCalEventGroupMetadataRaw(),
    fetchDestinationMonthsAsync(),
    fetchNatureMetadataAsync(),
    fetchOriginHolidaysAsync(),
    fetchReligionsAsync(),
    fetchReligionCountriesAsync(),
    fetchOrganizationsAsync(),
    fetchOrganizationCountriesAsync(),
    fetchOrganizationNamesAsync(),
  ]);
  _setConfigData({
    pageTypes,
    adminSettings,
    groupMeta,
    destinationMonths,
    natureMetadata,
    originHolidays,
    religions,
    religionCountries,
    organizations,
    organizationCountries,
    organizationNames,
  });
  await Promise.all([phenomenaP, bestTimeP]);
}

/**
 * Load the 11 universally-needed small config tables on demand: page_types,
 * admin_settings, cal_event_group_metadata, cal_destination_months,
 * cal_nature_metadata, cal_origin_holidays, cal_religions,
 * cal_religion_countries, cal_organizations, cal_organization_countries,
 * i18n_organization_names. Every entry point that also awaits preloadI18n /
 * preloadEvents / preloadGeo should await this too — these tables back
 * getNavConfig, getMinEventsThreshold, and the religion/organization/nature
 * lookup APIs.
 */
export function preloadConfig(): Promise<void> {
  if (!_preloadConfigPromise) {
    _preloadConfigPromise = _doPreloadConfig().catch(err => {
      _preloadConfigPromise = null;
      logError('preload.config.failed', errFields(err));
      throw err;
    });
  }
  return _preloadConfigPromise;
}

/** Test-only: clear the preload singleton so a fresh preloadConfig() actually re-fetches. */
export function _resetPreloadConfigForTesting(): void {
  _preloadConfigPromise = null;
}

/**
 * Called by preloadVisa() to write visa_requirements back into module scope
 * and rebuild the visa derived maps. Safe to call repeatedly.
 */
export function _setVisaData(data: { visaRequirements: VisaRequirement[] }): void {
  _visaRequirements = data.visaRequirements;
  _rebuildVisaIndexes();
}

let _preloadVisaPromise: Promise<void> | null = null;

async function _doPreloadVisa(): Promise<void> {
  const visaRequirements = await fetchVisaRequirementsAsync();
  _setVisaData({ visaRequirements });
}

/**
 * Load visa_requirements on demand. Wire into route handlers that render
 * visa-related SEO (VisaSeoSection, CrossCalendarSeoSection, PassportRanking,
 * VisaDestinationTable). visa_requirements is the largest of the remaining
 * tables (625K REST calls / 28d), so pages that don't render visa content
 * should skip preloadVisa.
 */
export function preloadVisa(): Promise<void> {
  if (!_preloadVisaPromise) {
    _preloadVisaPromise = _doPreloadVisa().catch(err => {
      _preloadVisaPromise = null;
      logError('preload.visa.failed', errFields(err));
      throw err;
    });
  }
  return _preloadVisaPromise;
}

/** Test-only: clear the preload singleton so a fresh preloadVisa() actually re-fetches. */
export function _resetPreloadVisaForTesting(): void {
  _preloadVisaPromise = null;
}

/** Config-table index rebuild. Called by _setConfigData (qoy-5). */
function _rebuildConfigIndexes(): void {
  _pageTypesMap.clear();
  _adminSettings.clear();
  _groupMetaMap.clear();
  _destMonthsByCode.clear();
  _destMonthByKey.clear();
  _natureMetaById.clear();
  _originHolidaysByCountry.clear();
  _relCountriesByCountry.clear();
  _relCountriesByReligion.clear();
  _orgsById.clear();
  _orgsByReligion.clear();
  _orgCountriesByCountry.clear();
  _orgNamesByOrg.clear();

  for (const row of _pageTypesRaw) _pageTypesMap.set(row.id, row);
  for (const row of _adminSettingsRaw) _adminSettings.set(row.key as string, row.value as string);

  for (const row of _groupMetaRaw) {
    _groupMetaMap.set(row.event_group, {
      eventGroup: row.event_group,
      displayName: row.display_name,
      description: row.description || null,
    });
  }

  for (const d of _destinationMonths) {
    let arr = _destMonthsByCode.get(d.destinationCode);
    if (!arr) { arr = []; _destMonthsByCode.set(d.destinationCode, arr); }
    arr.push(d);
    _destMonthByKey.set(`${d.destinationCode}:${d.month}`, d);
  }

  for (const m of _natureMetadata) _natureMetaById.set(m.eventId, m);

  for (const h of _originHolidays) {
    let arr = _originHolidaysByCountry.get(h.countryCode);
    if (!arr) { arr = []; _originHolidaysByCountry.set(h.countryCode, arr); }
    arr.push(h);
  }

  for (const rc of _religionCountries) {
    let byC = _relCountriesByCountry.get(rc.countryCode);
    if (!byC) { byC = []; _relCountriesByCountry.set(rc.countryCode, byC); }
    byC.push(rc);
    let byR = _relCountriesByReligion.get(rc.religionId);
    if (!byR) { byR = []; _relCountriesByReligion.set(rc.religionId, byR); }
    byR.push(rc);
  }

  for (const org of _organizations) {
    _orgsById.set(org.id, org);
    let arr = _orgsByReligion.get(org.religionId);
    if (!arr) { arr = []; _orgsByReligion.set(org.religionId, arr); }
    arr.push(org);
  }

  for (const oc of _organizationCountries) {
    let arr = _orgCountriesByCountry.get(oc.countryCode);
    if (!arr) { arr = []; _orgCountriesByCountry.set(oc.countryCode, arr); }
    arr.push(oc);
  }

  for (const on of _organizationNames) {
    let arr = _orgNamesByOrg.get(on.organizationId);
    if (!arr) { arr = []; _orgNamesByOrg.set(on.organizationId, arr); }
    arr.push(on);
  }
}

/** Visa-table index rebuild. Called by _setVisaData (qoy-5). */
function _rebuildVisaIndexes(): void {
  _visaReqMap.clear();
  _visaReqByOrigin.clear();
  for (const vr of _visaRequirements) {
    _visaReqMap.set(`${vr.origin}-${vr.destination}`, vr);
    let arr = _visaReqByOrigin.get(vr.origin);
    if (!arr) { arr = []; _visaReqByOrigin.set(vr.origin, arr); }
    arr.push(vr);
  }
}

/**
 * Academic year (start year) for a university event. An academic year spans two
 * calendar years (AY 2026/2027 = Aug 2026 – Jul 2027), so the event's `year`
 * field (the calendar year of its start date) is wrong for second-half events
 * (Jan–Jul), which would otherwise bucket onto the next academic year's page and
 * leak the prior year's tail onto this one. The event id encodes the academic
 * year start (e.g. `ugm-even-semester-start-2026` = AY 2026/2027), so prefer
 * that; fall back to a month heuristic (Aug+ = start year) when absent.
 */
export function academicYearForUniEvent(e: { id: string; dateStart: string; _year: number }): number {
  const m = /-(\d{4})$/.exec(e.id);
  if (m) return parseInt(m[1], 10);
  const month = parseInt(e.dateStart.slice(5, 7), 10);
  return month >= 8 ? e._year : e._year - 1;
}

/** Event + event-translation index rebuild. Called by _setEventsData (qoy-3). */
function _rebuildEventIndexes(): void {
  _eventsById.clear();
  _eventsByReligionSubLens.clear();
  _eventsByReligionCountry.clear();
  _eventsByNatureCountry.clear();
  _allNatureEvents.length = 0;
  _eventsByUniYear.clear();
  _eventsByUni.clear();
  _yearsByUni.clear();
  _eventsByCity.clear();
  _eventsByCountry.clear();
  _publicHolidaysByCountry.clear();
  _eventsByGroup.clear();
  _eventTranslationKeys.clear();
  _translatedTitles.clear();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _publicEvents = _events.map(({ _year, _vertical, _universityId, ...event }) => event);

  for (const e of _events) {
    _eventsById.set(e.id, e);

    if (e.lens === 'religion') {
      if (e.subLens) {
        let subArr = _eventsByReligionSubLens.get(e.subLens);
        if (!subArr) { subArr = []; _eventsByReligionSubLens.set(e.subLens, subArr); }
        subArr.push(e);
      }
      let rcArr = _eventsByReligionCountry.get(e.country);
      if (!rcArr) { rcArr = []; _eventsByReligionCountry.set(e.country, rcArr); }
      rcArr.push(e);
    }
    if (e.lens === 'nature') {
      _allNatureEvents.push(e);
      let ncArr = _eventsByNatureCountry.get(e.country);
      if (!ncArr) { ncArr = []; _eventsByNatureCountry.set(e.country, ncArr); }
      ncArr.push(e);
    }

    if (e._universityId) {
      const baseId = e._universityId;
      let allArr = _eventsByUni.get(baseId);
      if (!allArr) { allArr = []; _eventsByUni.set(baseId, allArr); }
      allArr.push(e);

      const academicYear = academicYearForUniEvent(e);
      const yearKey = `${baseId}:${academicYear}`;
      let yearArr = _eventsByUniYear.get(yearKey);
      if (!yearArr) { yearArr = []; _eventsByUniYear.set(yearKey, yearArr); }
      yearArr.push(e);

      let years = _yearsByUni.get(baseId);
      if (!years) { years = new Set(); _yearsByUni.set(baseId, years); }
      years.add(academicYear);
    }

    if (e.city && e.lens === 'city') {
      let arr = _eventsByCity.get(e.city);
      if (!arr) { arr = []; _eventsByCity.set(e.city, arr); }
      arr.push(e);
    }

    if (e.lens === 'country') {
      let arr = _eventsByCountry.get(e.country);
      if (!arr) { arr = []; _eventsByCountry.set(e.country, arr); }
      arr.push(e);

      if (isPublicHoliday(e)) {
        let phArr = _publicHolidaysByCountry.get(e.country);
        if (!phArr) { phArr = []; _publicHolidaysByCountry.set(e.country, phArr); }
        phArr.push(e);
      }
    }

    if (e.eventGroup) {
      let arr = _eventsByGroup.get(e.eventGroup);
      if (!arr) { arr = []; _eventsByGroup.set(e.eventGroup, arr); }
      arr.push(e);
    }
  }

  for (const r of _eventTranslationsRaw) {
    _eventTranslationKeys.add(`${r.event_id}:${r.lang}`);
    _translatedTitles.set(`${r.event_id}:${r.lang}`, r.title);
  }
}

// --- Public API (same interface as old data.ts) ---

export function getLenses(lang?: string): Lens[] {
  const lenses = lensesData as Lens[];
  if (!lang || lang === 'en') return lenses;
  return lenses.map(l => {
    const key = `ui.lens_${l.id.replace('-', '_')}`;
    const resolved = uiFn(key, lang);
    return resolved !== key ? { ...l, label: resolved } : l;
  });
}

export function getCountries(): Country[] {
  return _countries;
}

/** O(1) country lookup by code. Returns undefined if not found. */
export function getCountryByCode(code: string): Country | undefined {
  return _countriesByCode.get(code);
}

export function getCities(): City[] {
  return _cities;
}

/** O(1) city lookup by ID. Returns undefined if not found. */
export function getCityById(id: string): City | undefined {
  return _citiesById.get(id);
}

export function getUniversities(): University[] {
  return _universities;
}

/** O(1) university lookup by ID. Returns undefined if not found. */
export function getUniversityById(id: string): University | undefined {
  return _universitiesById.get(id);
}

export function getEventsForCountry(code: CountryCode): CalendarEvent[] {
  return _eventsByCountry.get(code) ?? [];
}

/** Returns all years that have event data, sorted ascending. */
export function getAvailableYears(): number[] {
  const years = new Set<number>();
  for (const e of _events) { years.add(e._year); }
  return [...years].sort();
}

/** Year-aware variant of getEventsForLens — filters results to a single year. */
export function getEventsForLensByYear(lens: LensId, entityId: string, year: number): CalendarEvent[] {
  const events = getEventsForLens(lens, entityId);
  return events.filter(e => e.year === year);
}

/** Returns public holidays for a country (public_holiday and national_day event types) */
export function getPublicHolidaysForCountry(code: CountryCode): CalendarEvent[] {
  return _publicHolidaysByCountry.get(code) ?? [];
}

export function getEventsForCity(cityId: string): CalendarEvent[] {
  const cityEvents = _eventsByCity.get(cityId) ?? [];
  // Fallback: include country-level public holidays when city has few specific events
  if (cityEvents.length < 5) {
    const city = _citiesById.get(cityId);
    if (city) {
      const holidays = getPublicHolidaysForCountry(city.country);
      // Merge without duplicates (by event id)
      const seen = new Set(cityEvents.map(e => e.id));
      const merged: CalendarEvent[] = [...cityEvents];
      for (const h of holidays) {
        if (!seen.has(h.id)) {
          merged.push(h);
          seen.add(h.id);
        }
      }
      return merged;
    }
  }
  return cityEvents;
}

/**
 * Count of an event city's GENUINE local events — the un-padded `_eventsByCity`
 * bucket, without the national-holiday fallback that getEventsForCity() adds for
 * thin cities. Page-generation gates use this so a city with zero real local
 * events isn't kept alive by duplicated national holidays. Refs: almanak-aqep.
 */
export function getLocalEventCountForCity(cityId: string): number {
  return _eventsByCity.get(cityId)?.length ?? 0;
}

export function getEventsForReligion(subLens: ReligionSubLensId): CalendarEvent[] {
  return _eventsByReligionSubLens.get(subLens) ?? [];
}

export function getEventsForNature(): CalendarEvent[] {
  return _allNatureEvents;
}

export function getEventsForReligionByCountry(code: CountryCode): CalendarEvent[] {
  return _eventsByReligionCountry.get(code) ?? [];
}

export function getEventsForNatureByCountry(code: CountryCode): CalendarEvent[] {
  return _eventsByNatureCountry.get(code) ?? [];
}

export function getEventsForUniversity(universityId: string): CalendarEvent[] {
  const [baseId, yearStr] = universityId.split(':');
  if (yearStr) {
    return _eventsByUniYear.get(`${baseId}:${parseInt(yearStr, 10)}`) ?? [];
  }
  return _eventsByUni.get(baseId) ?? [];
}

export function getAvailableAcademicYears(universityId: string): number[] {
  return [...(_yearsByUni.get(universityId) ?? [])].sort();
}

export function getAllEvents(): CalendarEvent[] {
  return _publicEvents;
}

export function getEventById(id: string): CalendarEvent | null {
  return _eventsById.get(id) ?? null;
}

export function getEventsForLens(lens: LensId, entityId: string): CalendarEvent[] {
  switch (lens) {
    case 'country':
      return getEventsForCountry(entityId as CountryCode);
    case 'city':
      return getEventsForCity(entityId);
    case 'religion': {
      const relParts = entityId.split(':');
      if (relParts.length > 1) {
        const countryCode = relParts[0];
        const subFilter = relParts[1];
        let events = getEventsForReligionByCountry(countryCode as CountryCode);
        if (subFilter !== 'all') {
          events = events.filter(e => e.subLens === subFilter);
        }
        return events;
      }
      return getEventsForReligion(entityId as ReligionSubLensId);
    }
    case 'nature': {
      const allNature = getEventsForNature();
      const parts = entityId.split(':');
      const maybeCountry = parts.length > 1 ? parts[0] : null;
      const subLensOrAll = parts.length > 1 ? parts[1] : parts[0];

      let filtered = allNature;
      if (maybeCountry && maybeCountry.length === 2) {
        filtered = filtered.filter(e => e.country === maybeCountry);
      }
      if (subLensOrAll && subLensOrAll !== 'all') {
        filtered = filtered.filter(e => e.subLens === subLensOrAll);
      }
      return filtered;
    }
    case 'academic':
      return getEventsForUniversity(entityId);
    case 'long-weekend': {
      const [countryCode, subType] = entityId.split(':');
      const countryEvents = getEventsForCountry(countryCode as CountryCode);
      const lws = getLongWeekends(countryEvents, countryCode as CountryCode);
      const filtered = subType === 'natural' ? lws.filter(lw => lw.leaveDaysNeeded === 0)
        : subType === 'bridge' ? lws.filter(lw => lw.leaveDaysNeeded > 0)
        : lws;
      return filtered.map(lw => lw.holiday);
    }
    default:
      return [];
  }
}

export function getEventsForOverlay(
  lenses: LensId[],
  entities: Record<string, string>
): CalendarEvent[] {
  const seen = new Set<string>();
  const merged: CalendarEvent[] = [];

  for (const lens of lenses) {
    const entityId = entities[lens];
    if (!entityId) continue;
    const events = getEventsForLens(lens, entityId);
    for (const event of events) {
      if (!seen.has(event.id)) {
        seen.add(event.id);
        merged.push(event);
      }
    }
  }

  return merged;
}

// --- I18n data accessors ---

export function getI18nLanguages(): I18nLanguage[] {
  return _i18nLanguages;
}

export function getI18nCountryNames(): I18nCountryName[] {
  return _i18nCountryNames;
}

export function getI18nPhrases(): I18nPhrase[] {
  return _i18nPhrases;
}

export function getI18nCityNames(): I18nCityName[] {
  return _i18nCityNames;
}

export function getI18nSubdivisionNames(): I18nSubdivisionName[] {
  return _i18nSubdivisionNames;
}

// --- Tier 1 lookup helpers for event content translation ---
// (_cityNameMap and _subdivNameMap are declared and populated in the
// prefetch block above; lookup functions below read from them.)

/** Get translated city name. Falls back to English, then to cityId. */
export function getCityName(cityId: string, lang: string): string {
  return _cityNameMap.get(`${cityId}:${lang}`)
    ?? _cityNameMap.get(`${cityId}:en`)
    ?? cityId;
}

/** Get translated subdivision name. Falls back to English, then to raw subdivision. */
export function getSubdivisionName(subdivision: string, lang: string): string {
  return _subdivNameMap.get(`${subdivision}:${lang}`)
    ?? _subdivNameMap.get(`${subdivision}:en`)
    ?? subdivision;
}

export function getI18nTemplates(): I18nTemplate[] {
  return _i18nTemplates;
}

export function getI18nCountryLanguages(): I18nCountryLanguage[] {
  return _i18nCountryLanguages;
}

/** Returns all events sharing the same event_group (cross-country siblings). */
/**
 * Collapse a holiday group's events to one representative per country. The same
 * holiday often has several rows for one country: a country-lens row plus a
 * religion/buddhist/hindu-lens row (the intentional dual-lens design, needed by
 * the religion hubs) and sometimes an alternate-title row ("Feast of the
 * Sacrifice" vs "Eid al-Adha"). Cross-country comparison / related views want a
 * single row per country, so prefer the country-lens public holiday, then any
 * country-lens row, then the lexically-first id for stability. Order is by each
 * country's first appearance. (almanak-bxid)
 */
export function oneEventPerCountry(events: CalendarEvent[]): CalendarEvent[] {
  const rank = (e: CalendarEvent) =>
    (e.lens === 'country' ? 0 : 2) + (e.type === 'public_holiday' ? 0 : 1);
  const best = new Map<string, CalendarEvent>();
  const order: string[] = [];
  for (const e of events) {
    const cur = best.get(e.country);
    if (!cur) { order.push(e.country); best.set(e.country, e); continue; }
    if (rank(e) < rank(cur) || (rank(e) === rank(cur) && e.id < cur.id)) best.set(e.country, e);
  }
  return order.map(c => best.get(c)!);
}

export function getRelatedEventsByGroup(groupId: string): CalendarEvent[] {
  return oneEventPerCountry(_eventsByGroup.get(groupId) ?? []);
}

/** Returns metadata for an event group (display name, description). */
export function getEventGroupMetadata(groupId: string): EventGroupMetadata | null {
  return _groupMetaMap.get(groupId) ?? null;
}

/** Returns all distinct event_group values across all events. */
export function getDistinctEventGroups(): string[] {
  const groups = new Set<string>();
  for (const e of _publicEvents) {
    if (e.eventGroup) groups.add(e.eventGroup);
  }
  return [...groups].sort();
}

// --- Destination month accessors ---

/** Returns destination month data for a specific country+month, or null if not seeded. */
export function getDestinationMonth(code: string, month: number): DestinationMonth | null {
  return _destMonthByKey.get(`${code}:${month}`) ?? null;
}

/** Returns all 12 months of destination data for a country. */
export function getDestinationMonths(code: string): DestinationMonth[] {
  return _destMonthsByCode.get(code) ?? [];
}

// --- Nature metadata accessors ---

/** Returns nature metadata for a specific event ID, or null if not present. */
export function getNatureMetadata(eventId: string): NatureMetadata | null {
  return _natureMetaById.get(eventId) ?? null;
}

/** Returns all nature metadata entries for events in a given country. */
export function getNatureMetadataByCountry(code: CountryCode): NatureMetadata[] {
  const natureEvents = getEventsForNatureByCountry(code);
  const eventIds = new Set(natureEvents.map(e => e.id));
  return _natureMetadata.filter(m => eventIds.has(m.eventId));
}

// --- Origin holiday accessors ---

/** Returns origin holidays for a country code (US, GB, AU, etc.). */
export function getOriginHolidays(code: string): OriginHoliday[] {
  return _originHolidaysByCountry.get(code) ?? [];
}

// --- Visa requirement accessors ---

/** Returns visa requirement for a specific origin→destination pair, or null. */
export function getVisaRequirement(origin: string, destination: string): VisaRequirement | null {
  return _visaReqMap.get(`${origin}-${destination}`) ?? null;
}

/** Returns all visa requirements for a given origin country. */
export function getVisaRequirementsForOrigin(origin: string): VisaRequirement[] {
  return _visaReqByOrigin.get(origin) ?? [];
}

// --- Religion demographics accessors ---

/** Returns the master list of religions, sorted by sort_order. */
export function getReligions(): Religion[] {
  return _religions;
}

/** Returns religion demographics for a specific country. */
export function getReligionsByCountry(code: CountryCode): ReligionCountry[] {
  return _relCountriesByCountry.get(code) ?? [];
}

/** Returns all country demographics for a specific religion. */
export function getCountriesByReligion(religionId: string): ReligionCountry[] {
  return _relCountriesByReligion.get(religionId) ?? [];
}

// --- Organization accessors ---

/** Returns the master list of organizations. */
export function getOrganizations(): Organization[] {
  return _organizations;
}

/** Returns organizations present in a specific country (with their OrganizationCountry join data). */
export function getOrganizationsByCountry(code: CountryCode): (Organization & { presence: OrganizationCountry })[] {
  const joins = _orgCountriesByCountry.get(code) ?? [];
  return joins
    .map(oc => {
      const org = _orgsById.get(oc.organizationId);
      return org ? { ...org, presence: oc } : null;
    })
    .filter((o): o is Organization & { presence: OrganizationCountry } => o !== null);
}

/** Returns all organizations for a specific religion. */
export function getOrganizationsByReligion(religionId: string): Organization[] {
  return _orgsByReligion.get(religionId) ?? [];
}

/** Returns official date-setting organizations for a country, optionally filtered by religion. */
export function getDateAuthorities(countryCode: CountryCode, religionId?: string): (Organization & { presence: OrganizationCountry })[] {
  return getOrganizationsByCountry(countryCode)
    .filter(o => o.presence.isDateAuthority && (!religionId || o.religionId === religionId));
}

/** Returns a single organization by ID, or null if not found. */
export function getOrganization(id: string): Organization | null {
  return _orgsById.get(id) ?? null;
}

/** Returns i18n names for an organization. */
export function getOrganizationNames(orgId: string): OrganizationName[] {
  return _orgNamesByOrg.get(orgId) ?? [];
}

// --- Runtime queries (async, not prefetched) ---

export interface CachedEventTranslation {
  title: string;
  titleLocal: string | null;
  notes: string | null;
  complianceRisk: string | null;
}

// _eventTranslationKeys and _translatedTitles are declared and populated in
// the prefetch block above; the sync check / lookup functions below read
// from them. Used by event slug registry to filter out untranslated language
// variants before they reach generateStaticParams / sitemaps / hreflang
// alternates.

/** Synchronous check: does a translation exist for this event in this language? */
export function hasEventTranslation(eventId: string, lang: string): boolean {
  if (lang === 'en') return true;
  return _eventTranslationKeys.has(`${eventId}:${lang}`);
}

/** Synchronous translated title lookup. Returns translated title or English fallback. */
export function getTranslatedEventTitle(eventId: string, lang: string, fallback: string): string {
  if (lang === 'en') return fallback;
  return _translatedTitles.get(`${eventId}:${lang}`) ?? fallback;
}

/** Fetches a cached event translation from cal_event_translations (runtime query). */
export async function getCachedEventTranslation(
  eventId: string,
  lang: string,
): Promise<CachedEventTranslation | null> {
  if (lang === 'en') return null;
  const { data } = await sb
    .from('cal_event_translations')
    .select('title, title_local, notes, compliance_risk')
    .eq('event_id', eventId)
    .eq('lang', lang)
    .single();
  if (!data) return null;
  return {
    title: data.title,
    titleLocal: data.title_local ?? null,
    notes: data.notes ?? null,
    complianceRisk: data.compliance_risk ?? null,
  };
}

// --- Per-table async getter API (substrate for the qoy-2..5 migration) ---
//
// Each fetchXxxAsync() reads from a per-table cache backed by
// next/cache.unstable_cache with 1h revalidation. Callers migrating off the
// sync API switch from `getXxx()` to `await fetchXxxAsync()`. Once all sync
// callers are gone (qoy-6), the top-level prefetch can be removed entirely.

const _CACHE_TTL_SECONDS = 3600;

function _makeCachedRaw<T extends AlmanakTableName>(table: T) {
  return unstable_cache(
    async () => fetchAll(sb, table),
    [`db-supabase:${table}`],
    { revalidate: _CACHE_TTL_SECONDS, tags: [table] },
  );
}

const _cachedCalEventsRaw = _makeCachedRaw('cal_events');
const _cachedCalCountriesRaw = _makeCachedRaw('cal_countries');
const _cachedCalCitiesRaw = _makeCachedRaw('cal_cities');
const _cachedCalUniversitiesRaw = _makeCachedRaw('cal_universities');
const _cachedI18nLanguagesRaw = _makeCachedRaw('i18n_languages');
const _cachedI18nCountryNamesRaw = _makeCachedRaw('i18n_country_names');
const _cachedI18nPhrasesRaw = _makeCachedRaw('i18n_phrases');
const _cachedI18nCityNamesRaw = _makeCachedRaw('i18n_city_names');
const _cachedI18nSubdivisionNamesRaw = _makeCachedRaw('i18n_subdivision_names');
const _cachedI18nTemplatesRaw = _makeCachedRaw('i18n_templates');
const _cachedCalEventGroupMetadataRaw = _makeCachedRaw('cal_event_group_metadata');
const _cachedCalDestinationMonthsRaw = _makeCachedRaw('cal_destination_months');
const _cachedCalNatureMetadataRaw = _makeCachedRaw('cal_nature_metadata');
const _cachedCalOriginHolidaysRaw = _makeCachedRaw('cal_origin_holidays');
const _cachedVisaRequirementsRaw = _makeCachedRaw('visa_requirements');
const _cachedCalReligionsRaw = _makeCachedRaw('cal_religions');
const _cachedCalReligionCountriesRaw = _makeCachedRaw('cal_religion_countries');
const _cachedI18nCountryLanguagesRaw = _makeCachedRaw('i18n_country_languages');
const _cachedPageTypesRaw = _makeCachedRaw('page_types');
const _cachedCalOrganizationsRaw = _makeCachedRaw('cal_organizations');
const _cachedCalOrganizationCountriesRaw = _makeCachedRaw('cal_organization_countries');
const _cachedI18nOrganizationNamesRaw = _makeCachedRaw('i18n_organization_names');
const _cachedCalEventTranslationsRaw = _makeCachedRaw('cal_event_translations');

export async function fetchEventsAsync(): Promise<CalendarEvent[]> {
  const raw = await _cachedCalEventsRaw();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return dedupeEventRowsById(raw).map(rowToInternalEvent).map(({ _year, _vertical, _universityId, ...e }) => e);
}

export async function fetchCountriesAsync(): Promise<Country[]> {
  return (await _cachedCalCountriesRaw()).map(rowToCountry);
}

export async function fetchCitiesAsync(): Promise<City[]> {
  return (await _cachedCalCitiesRaw()).map(rowToCity);
}

export async function fetchUniversitiesAsync(): Promise<University[]> {
  return (await _cachedCalUniversitiesRaw()).map(rowToUniversity);
}

export async function fetchI18nLanguagesAsync(): Promise<I18nLanguage[]> {
  return (await _cachedI18nLanguagesRaw()).map(rowToI18nLanguage);
}

export async function fetchI18nCountryNamesAsync(): Promise<I18nCountryName[]> {
  return (await _cachedI18nCountryNamesRaw()).map(rowToI18nCountryName);
}

export async function fetchI18nPhrasesAsync(): Promise<I18nPhrase[]> {
  return (await _cachedI18nPhrasesRaw()).map(rowToI18nPhrase);
}

export async function fetchI18nCityNamesAsync(): Promise<I18nCityName[]> {
  return (await _cachedI18nCityNamesRaw()).map(rowToI18nCityName);
}

export async function fetchI18nSubdivisionNamesAsync(): Promise<I18nSubdivisionName[]> {
  return (await _cachedI18nSubdivisionNamesRaw()).map(rowToI18nSubdivisionName);
}

export async function fetchI18nTemplatesAsync(): Promise<I18nTemplate[]> {
  return (await _cachedI18nTemplatesRaw()).map(rowToI18nTemplate);
}

export async function fetchI18nCountryLanguagesAsync(): Promise<I18nCountryLanguage[]> {
  return (await _cachedI18nCountryLanguagesRaw()).map(rowToI18nCountryLanguage);
}

export async function fetchDestinationMonthsAsync(): Promise<DestinationMonth[]> {
  return (await _cachedCalDestinationMonthsRaw()).map(rowToDestinationMonth);
}

export async function fetchNatureMetadataAsync(): Promise<NatureMetadata[]> {
  return (await _cachedCalNatureMetadataRaw()).map(rowToNatureMetadata);
}

export async function fetchOriginHolidaysAsync(): Promise<OriginHoliday[]> {
  return (await _cachedCalOriginHolidaysRaw()).map(rowToOriginHoliday);
}

export async function fetchVisaRequirementsAsync(): Promise<VisaRequirement[]> {
  return (await _cachedVisaRequirementsRaw()).map(rowToVisaRequirement);
}

export async function fetchReligionsAsync(): Promise<Religion[]> {
  return (await _cachedCalReligionsRaw()).map(rowToReligion);
}

export async function fetchReligionCountriesAsync(): Promise<ReligionCountry[]> {
  return (await _cachedCalReligionCountriesRaw()).map(rowToReligionCountry);
}

export async function fetchOrganizationsAsync(): Promise<Organization[]> {
  return (await _cachedCalOrganizationsRaw()).map(rowToOrganization);
}

export async function fetchOrganizationCountriesAsync(): Promise<OrganizationCountry[]> {
  return (await _cachedCalOrganizationCountriesRaw()).map(rowToOrganizationCountry);
}

export async function fetchOrganizationNamesAsync(): Promise<OrganizationName[]> {
  return (await _cachedI18nOrganizationNamesRaw()).map(rowToOrganizationName);
}

export async function fetchEventGroupMetadataAsync(): Promise<EventGroupMetadata[]> {
  return (await _cachedCalEventGroupMetadataRaw()).map(row => ({
    eventGroup: row.event_group,
    displayName: row.display_name,
    description: row.description || null,
  }));
}

export async function fetchPageTypesAsync(): Promise<RowOf<'page_types'>[]> {
  return _cachedPageTypesRaw();
}

export async function fetchEventTranslationsAsync(): Promise<RowOf<'cal_event_translations'>[]> {
  return _cachedCalEventTranslationsRaw();
}
